import { graphql } from '../../graphql/graphql';
import { weblinkFragment } from '../../graphql/fragments/weblinkFragment';

const schoolPageHelmetFragment = graphql(`
  fragment SchoolPageHelmetFragment on PropertyPage_Helmet {
    title
    canonical
    description
    siteName
    seoSchemas
    resourceHints {
      rel
      href
      as
      type
      crossorigin
    }
  }
`);

const schoolFactsFragment = graphql(
  `
    fragment SchoolFactsFragment on SchoolPage_SchoolFacts {
      title
      subtitle
      facts {
        title
        subtitle
      }
      naplanResultsLink {
        ...WebLink
      }
    }
  `,
  [weblinkFragment],
);

const schoolCatchmentMapFragment = graphql(`
  fragment SchoolCatchmentMapFragment on SchoolPage_SchoolCatchmentMap {
    title
    pins {
      coordinates {
        latitude
        longitude
      }
    }
    schoolCatchment {
      type
      features {
        type
        geometry {
          type
          coordinates
        }
      }
    }
    tracking {
      changeZoomLevel {
        schema
        data {
          event_name
          interaction_type
          in_page_source
        }
      }
      toggleFullscreen {
        schema
        data {
          event_name
          interaction_type
          in_page_source
        }
      }
    }
  }
`);

const propertiesRecentlySoldFragment = graphql(`
  fragment PropertiesRecentlySoldFragment on PropertyPage_PropertyCollection {
    title
    subtitle
    properties {
      image {
        alt
        caption
        jpgUri: uri(width: 224, height: 126, imageMode: Crop, progressive: true)
        webpUri: uri(
          width: 224
          height: 126
          format: $imageFormatWebp
          imageMode: Crop
          progressive: true
        )
      }
      badgeV2 {
        text
        variant
      }
      priceV2 {
        display
        sourceLevel
      }
      address
      pathV2
      clickThroughTracking {
        pathname
        page
        element
        sourceType
      }
      features {
        iconNameV2
        title
        hoverTitle
        valueWithUnit
      }
      soldDate
    }
    tracking {
      showMoreProperties {
        schema
        data {
          event_name
          in_page_source
          user_action
        }
      }
    }
  }
`);

const nearbySchoolsFragment = graphql(
  `
    fragment NearbySchoolsFragment on SchoolCollection {
      title
      subtitle
      schoolFactHeader
      schools {
        schoolName
        isDefaultOpen
        isZoned
        schoolFacts {
          title
          subtitle
        }
        webLinks {
          ...WebLink
        }
        tracking {
          expand {
            schema
            data {
              event_name
              in_page_source
              user_action
              accordion_copy
            }
          }
          collapse {
            schema
            data {
              event_name
              in_page_source
              user_action
              accordion_copy
            }
          }
        }
      }
    }
  `,
  [weblinkFragment],
);

export const contentOverviewFragment = graphql(
  `
    fragment SchoolPage_ContentOverview on SchoolPage_ContentOverview {
      __typename
      title
      helmet {
        ...SchoolPageHelmetFragment
      }
      schoolFacts {
        ...SchoolFactsFragment
      }
      schoolCatchmentMap {
        ...SchoolCatchmentMapFragment
      }
      catchmentDisclaimer {
        text
      }
      propertiesRecentlySold {
        ...PropertiesRecentlySoldFragment
      }
      nearbySchools {
        ...NearbySchoolsFragment
      }
    }
  `,
  [
    schoolPageHelmetFragment,
    schoolFactsFragment,
    schoolCatchmentMapFragment,
    propertiesRecentlySoldFragment,
    nearbySchoolsFragment,
  ],
);

export const propertyCollectionPropertyFragment = graphql(`
  fragment PropertyCollectionPropertyFragment on PropertyPage_PropertyCollection_Property {
    image {
      alt
      caption
      jpgUri: uri(width: 544, height: 306, imageMode: Crop, progressive: true)
      webpUri: uri(
        width: 544
        height: 306
        format: $imageFormatWebp
        imageMode: Crop
        progressive: true
      )
    }
    badgeV2 {
      text
      variant
    }
    priceV2 {
      display
      sourceLevel
    }
    address
    pathV2
    clickThroughTracking {
      pathname
      page
      element
      sourceType
    }
    features {
      iconNameV2
      title
      hoverTitle
      valueWithUnit
    }
  }
`);

export const contentPropertiesForSaleFragment = graphql(
  `
    fragment SchoolPage_PropertiesForSale on SchoolPage_PropertiesForSale {
      __typename
      title
      helmet {
        ...SchoolPageHelmetFragment
      }
      propertyCollection {
        properties(page: $page) {
          nodes {
            ...PropertyCollectionPropertyFragment
          }
          pageInfo {
            prevPage
            nextPage
          }
        }
      }
    }
  `,
  [schoolPageHelmetFragment, propertyCollectionPropertyFragment],
);

export const contentPropertiesForRentFragment = graphql(
  `
    fragment SchoolPage_PropertiesForRent on SchoolPage_PropertiesForRent {
      __typename
      title
      helmet {
        ...SchoolPageHelmetFragment
      }
      propertyCollection {
        properties(page: $page) {
          nodes {
            ...PropertyCollectionPropertyFragment
          }
          pageInfo {
            prevPage
            nextPage
          }
        }
      }
    }
  `,
  [schoolPageHelmetFragment, propertyCollectionPropertyFragment],
);

export const schoolPageBySlugTabContentQuery = graphql(
  `
    query SchoolPageBySlugTabContent(
      $id: ID!
      $slug: Uri!
      $path: SchoolPageTabPath!
      $imageFormatWebp: ImageFormat!
      $page: Int!
    ) {
      schoolPageBySlug(id: $id, slug: $slug) {
        id
        tabContent(path: $path) {
          ... on SchoolPage_ContentOverview {
            ...SchoolPage_ContentOverview
          }

          ... on SchoolPage_PropertiesForSale {
            ...SchoolPage_PropertiesForSale
          }

          ... on SchoolPage_PropertiesForRent {
            ...SchoolPage_PropertiesForRent
          }
        }
      }
    }
  `,
  [contentOverviewFragment, contentPropertiesForSaleFragment, contentPropertiesForRentFragment],
);
